import { useState } from 'react';
import { useRecordConfig, useCreateRecord, useCustomerRecords, useDeleteRecord, useUpdateRecord } from './recordHooks';
import { RecordForm } from './RecordForm';
import type { CustomerRecord } from './recordTypes';

interface RecordListProps {
  customerId: string;
}

/** Per-customer configurable records (replaces the old hardcoded pet list). */
export function RecordList({ customerId }: RecordListProps) {
  const { data: config } = useRecordConfig();
  const { data: records, isLoading } = useCustomerRecords(customerId);
  const create = useCreateRecord(customerId);
  const update = useUpdateRecord(customerId);
  const remove = useDeleteRecord(customerId);

  const [mode, setMode] = useState<
    { kind: 'add' } | { kind: 'edit'; record: CustomerRecord } | { kind: 'idle' }
  >({ kind: 'idle' });

  if (!config || isLoading) return <div className="text-sm text-slate-500">Loading…</div>;

  const singular = config.singularLabel;
  const plural = config.pluralLabel;
  const fields = config.fields; // active, sorted
  const primaryKey = fields.find((f) => f.isPrimaryName)?.key;
  const secondary = fields.filter((f) => !f.isPrimaryName);

  const onCreate = async (data: Record<string, string>) => {
    await create.mutateAsync(data);
    setMode({ kind: 'idle' });
  };
  const onUpdate = async (recordId: string, data: Record<string, string>) => {
    await update.mutateAsync({ recordId, data });
    setMode({ kind: 'idle' });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800">{plural}</h2>
        {mode.kind === 'idle' && (
          <button
            type="button"
            onClick={() => setMode({ kind: 'add' })}
            className="text-sky-600 hover:text-sky-700 text-sm font-medium"
          >
            + Add {singular.toLowerCase()}
          </button>
        )}
      </div>

      {mode.kind === 'add' && (
        <div className="border border-slate-200 rounded-lg p-4 bg-white">
          <h3 className="font-medium text-slate-800 mb-3">New {singular.toLowerCase()}</h3>
          <RecordForm
            fields={fields}
            submitLabel={`Add ${singular.toLowerCase()}`}
            submitting={create.isPending}
            onSubmit={onCreate}
            onCancel={() => setMode({ kind: 'idle' })}
          />
        </div>
      )}

      {mode.kind === 'edit' && (
        <div className="border border-slate-200 rounded-lg p-4 bg-white">
          <h3 className="font-medium text-slate-800 mb-3">Edit {mode.record.primaryName}</h3>
          <RecordForm
            fields={fields}
            defaultValues={mode.record.data}
            submitLabel="Save"
            submitting={update.isPending}
            onSubmit={(data) => onUpdate(mode.record.id, data)}
            onCancel={() => setMode({ kind: 'idle' })}
          />
        </div>
      )}

      {records && records.length === 0 && mode.kind !== 'add' && (
        <p className="text-sm text-slate-500 italic">No {plural.toLowerCase()} yet.</p>
      )}

      <ul className="space-y-2">
        {records?.map((r) => (
          <li
            key={r.id}
            className="border border-slate-200 rounded-lg px-4 py-3 bg-white flex items-start justify-between gap-4"
          >
            <div className="min-w-0">
              <div className="font-medium text-slate-800">{r.primaryName}</div>
              <div className="text-xs text-slate-500 space-y-0.5 mt-1">
                {secondary
                  .filter((f) => r.data[f.key])
                  .map((f) => (
                    <div key={f.id}>
                      <span className="text-slate-400">{f.label}:</span> {r.data[f.key]}
                    </div>
                  ))}
              </div>
            </div>
            <div className="flex gap-3 text-sm shrink-0">
              <button
                type="button"
                onClick={() => setMode({ kind: 'edit', record: r })}
                className="text-sky-600 hover:text-sky-700"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => {
                  if (confirm(`Remove ${r.primaryName}?`)) remove.mutate(r.id);
                }}
                className="text-red-600 hover:text-red-700"
              >
                Remove
              </button>
            </div>
          </li>
        ))}
      </ul>
      {primaryKey === undefined && (
        <p className="text-xs text-amber-700">No primary field configured — check Settings.</p>
      )}
    </div>
  );
}
